/* Built from dev/crossword-engine.js + dev/crossword-ui.js — edit those, then re-run dev/build.sh */
/*
 * Stiles Crossword Layout Engine
 * ------------------------------
 * Deterministic, scored crossword layout generation.
 *
 * Grid cell shape is backward compatible with the previous engine:
 *   grid[r][c] = null | { char, across: {is_start_of_word,index}|null, down: {...}|null }
 *
 * Public API (attached to window.CrosswordEngine in the browser, module.exports in Node):
 *   normalizeWords(rawWords) -> { words, errors }
 *   generate(words, opts)    -> { ok, grid, placements, unplaced, letterCounts, tileCount, error }
 *   generateVariants(words, k, opts) -> array of up to k distinct results, best first
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CrosswordEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MAX_WORD_LEN = 24;
  var MAX_WORDS = 15;
  var GRID = 48; // working grid; minimized before returning

  // --- seeded PRNG (mulberry32) so layouts are reproducible & testable ---
  function rng(seed) {
    var t = seed >>> 0;
    return function () {
      t += 0x6D2B79F5;
      var r = Math.imul(t ^ (t >>> 15), 1 | t);
      r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  // --- input normalization -------------------------------------------------
  // Accepts array of raw strings. Uppercases, trims, strips diacritics,
  // dedupes, and reports precise per-word errors instead of a blanket alert.
  function normalizeWords(rawWords) {
    var words = [];
    var seen = {};
    var errors = [];
    (rawWords || []).forEach(function (raw) {
      if (raw == null) return;
      var w = String(raw).trim().toUpperCase();
      if (w === '') return;
      // strip diacritics: JOSÉ -> JOSE
      if (w.normalize) w = w.normalize('NFD').replace(/[̀-ͯ]/g, '');
      if (!/^[A-Z]+$/.test(w)) {
        errors.push({ word: raw, code: 'invalid_chars', message: '"' + raw + '" can only contain letters A–Z.' });
        return;
      }
      if (w.length === 1) {
        errors.push({ word: raw, code: 'too_short', message: '"' + raw + '" needs at least 2 letters.' });
        return;
      }
      if (w.length > MAX_WORD_LEN) {
        errors.push({ word: raw, code: 'too_long', message: '"' + raw + '" is longer than ' + MAX_WORD_LEN + ' letters.' });
        return;
      }
      if (seen[w]) return; // silently dedupe repeats
      seen[w] = true;
      words.push(w);
    });
    if (words.length > MAX_WORDS) {
      errors.push({ code: 'too_many', message: 'Please use ' + MAX_WORDS + ' words or fewer (you have ' + words.length + ').' });
      words = words.slice(0, MAX_WORDS);
    }
    return { words: words, errors: errors };
  }

  // --- core placement ------------------------------------------------------
  function makeGrid() {
    var g = new Array(GRID);
    for (var r = 0; r < GRID; r++) g[r] = new Array(GRID).fill(null);
    return g;
  }

  function inBounds(r, c) { return r >= 0 && r < GRID && c >= 0 && c < GRID; }

  function cellAt(grid, r, c) { return inBounds(r, c) ? grid[r][c] : null; }

  // Legality check. Returns false or the number of intersections (>=0).
  function canPlace(grid, word, row, col, dir) {
    var dr = dir === 'down' ? 1 : 0;
    var dc = dir === 'down' ? 0 : 1;
    var endR = row + dr * (word.length - 1);
    var endC = col + dc * (word.length - 1);
    if (!inBounds(row, col) || !inBounds(endR, endC)) return false;

    // cell before start and after end must be empty
    if (cellAt(grid, row - dr, col - dc)) return false;
    if (cellAt(grid, endR + dr, endC + dc)) return false;

    var intersections = 0;
    for (var i = 0; i < word.length; i++) {
      var r = row + dr * i, c = col + dc * i;
      var cell = grid[r][c];
      if (cell) {
        if (cell.char !== word.charAt(i)) return false;        // conflicting letter
        if (cell[dir]) return false;                            // a word already runs this direction here
        intersections++;
      } else {
        // an empty cell may not touch letters on its perpendicular sides
        if (dir === 'across') {
          if (cellAt(grid, r - 1, c) || cellAt(grid, r + 1, c)) return false;
        } else {
          if (cellAt(grid, r, c - 1) || cellAt(grid, r, c + 1)) return false;
        }
      }
    }
    if (intersections === word.length) return false; // full overlay of an existing word
    return intersections;
  }

  function place(grid, charIndex, word, wordIndex, row, col, dir) {
    var dr = dir === 'down' ? 1 : 0;
    var dc = dir === 'down' ? 0 : 1;
    for (var i = 0; i < word.length; i++) {
      var r = row + dr * i, c = col + dc * i;
      if (!grid[r][c]) {
        grid[r][c] = { char: word.charAt(i), across: null, down: null };
        var ch = word.charAt(i);
        (charIndex[ch] = charIndex[ch] || []).push({ row: r, col: c });
      }
      grid[r][c][dir] = { is_start_of_word: i === 0, index: wordIndex };
    }
  }

  // Enumerate every legal placement of `word` that intersects existing letters.
  function candidatesFor(grid, charIndex, word) {
    var out = [];
    for (var i = 0; i < word.length; i++) {
      var spots = charIndex[word.charAt(i)];
      if (!spots) continue;
      for (var j = 0; j < spots.length; j++) {
        var p = spots[j];
        var a = canPlace(grid, word, p.row, p.col - i, 'across');
        if (a !== false && a > 0) out.push({ row: p.row, col: p.col - i, dir: 'across', hits: a });
        var d = canPlace(grid, word, p.row - i, p.col, 'down');
        if (d !== false && d > 0) out.push({ row: p.row - i, col: p.col, dir: 'down', hits: d });
      }
    }
    return out;
  }

  function bounds(grid) {
    var rMin = GRID, rMax = -1, cMin = GRID, cMax = -1;
    for (var r = 0; r < GRID; r++) for (var c = 0; c < GRID; c++) {
      if (grid[r][c]) {
        if (r < rMin) rMin = r; if (r > rMax) rMax = r;
        if (c < cMin) cMin = c; if (c > cMax) cMax = c;
      }
    }
    return { rMin: rMin, rMax: rMax, cMin: cMin, cMax: cMax };
  }

  // Score a single candidate placement in context: favor intersections and
  // compactness (small bounding-box growth, square aspect).
  function scorePlacement(grid, cand, word) {
    var b = bounds(grid);
    var dr = cand.dir === 'down' ? 1 : 0;
    var dc = cand.dir === 'down' ? 0 : 1;
    var endR = cand.row + dr * (word.length - 1);
    var endC = cand.col + dc * (word.length - 1);
    var nrMin = Math.min(b.rMin, cand.row), nrMax = Math.max(b.rMax, endR);
    var ncMin = Math.min(b.cMin, cand.col), ncMax = Math.max(b.cMax, endC);
    var h = nrMax - nrMin + 1, w = ncMax - ncMin + 1;
    var growth = (h * w) - ((b.rMax - b.rMin + 1) * (b.cMax - b.cMin + 1));
    var aspect = Math.min(h, w) / Math.max(h, w); // 1 = square
    return cand.hits * 40 - growth * 1.5 + aspect * 20;
  }

  function attempt(words, seed) {
    var rand = rng(seed);
    var grid = makeGrid();
    var charIndex = {};
    var placements = [];
    var unplaced = [];

    // longest first gives the layout a spine
    var order = words.map(function (w, i) { return { w: w, i: i }; })
      .sort(function (a, b) { return b.w.length - a.w.length; });

    var first = order[0];
    var dir = rand() < 0.5 ? 'across' : 'down';
    var row = Math.floor(GRID / 2), col = Math.floor(GRID / 2);
    if (dir === 'across') col -= Math.floor(first.w.length / 2);
    else row -= Math.floor(first.w.length / 2);
    place(grid, charIndex, first.w, first.i, row, col, dir);
    placements.push({ word: first.w, index: first.i, row: row, col: col, dir: dir, hits: 0 });

    var queue = order.slice(1);
    var progress = true;
    while (queue.length && progress) {
      progress = false;
      var next = [];
      for (var q = 0; q < queue.length; q++) {
        var item = queue[q];
        var cands = candidatesFor(grid, charIndex, item.w);
        if (!cands.length) { next.push(item); continue; }
        cands.forEach(function (c) { c.score = scorePlacement(grid, c, item.w); });
        cands.sort(function (a, b) { return b.score - a.score; });
        // weighted pick among the top few for variety between seeds
        var k = Math.min(3, cands.length);
        var pick = cands[Math.floor(rand() * rand() * k)]; // biased toward best
        place(grid, charIndex, item.w, item.i, pick.row, pick.col, pick.dir);
        placements.push({ word: item.w, index: item.i, row: pick.row, col: pick.col, dir: pick.dir, hits: pick.hits });
        progress = true;
      }
      queue = next;
    }
    queue.forEach(function (item) { unplaced.push(item.w); });

    // minimize grid and rebase placements into minimized coordinates
    var b = bounds(grid);
    var minimized = [];
    for (var r = b.rMin; r <= b.rMax; r++) minimized.push(grid[r].slice(b.cMin, b.cMax + 1));
    placements.forEach(function (p) { p.row -= b.rMin; p.col -= b.cMin; });

    // overall layout score: everything placed >> intersections >> squareness >> density
    var h = minimized.length, w = minimized[0] ? minimized[0].length : 0;
    var filled = 0;
    minimized.forEach(function (rw) { rw.forEach(function (cl) { if (cl) filled++; }); });
    var totalHits = placements.reduce(function (s, p) { return s + p.hits; }, 0);
    var score = (words.length - unplaced.length) * 1000
      + totalHits * 30
      + (Math.min(h, w) / Math.max(h, w || 1)) * 40
      + (filled / (h * w || 1)) * 20;

    return { grid: minimized, placements: placements, unplaced: unplaced, score: score,
             offsetRow: b.rMin, offsetCol: b.cMin };
  }

  function signature(result) {
    // orientation-normalized signature so RE-GENERATE variants feel distinct
    return result.grid.map(function (row) {
      return row.map(function (c) { return c ? c.char : '.'; }).join('');
    }).join('|');
  }

  function letterCounts(result) {
    var counts = {};
    var total = 0;
    result.grid.forEach(function (row) {
      row.forEach(function (cell) {
        if (cell) { counts[cell.char] = (counts[cell.char] || 0) + 1; total++; }
      });
    });
    return { counts: counts, total: total };
  }

  // Single word: lay it out as one across row (still a sellable product).
  function singleWordResult(word) {
    var grid = [word.split('').map(function (ch, i) {
      return { char: ch, across: { is_start_of_word: i === 0, index: 0 }, down: null };
    })];
    return { grid: grid, placements: [{ word: word, index: 0, row: 0, col: 0, dir: 'across', hits: 0 }],
             unplaced: [], score: 0 };
  }

  function generate(words, opts) {
    opts = opts || {};
    var attempts = opts.attempts || 40;
    var seed = (opts.seed == null ? 1 : opts.seed) | 0;

    if (!words || !words.length) {
      return { ok: false, error: { code: 'no_words', message: 'Type at least one word to get started.' } };
    }
    if (words.length === 1) {
      var single = singleWordResult(words[0]);
      var lc1 = letterCounts(single);
      return { ok: true, grid: single.grid, placements: single.placements, unplaced: [],
               letterCounts: lc1.counts, tileCount: lc1.total, single: true };
    }

    var best = null;
    for (var i = 0; i < attempts; i++) {
      var res = attempt(words, seed + i * 7919);
      if (!best || res.score > best.score) best = res;
      if (best.unplaced.length === 0 && i >= Math.min(attempts - 1, 12) && best.score >= res.score) {
        // good enough after a dozen fully-placed tries
      }
    }

    var lc = letterCounts(best);
    var out = { ok: best.unplaced.length === 0, grid: best.grid, placements: best.placements,
                unplaced: best.unplaced, letterCounts: lc.counts, tileCount: lc.total };
    if (!out.ok) {
      // distinguish "shares no letters" from "shares letters but no legal layout"
      var placedWords = best.placements.map(function (p) { return p.word; });
      var noShare = [], noFit = [];
      best.unplaced.forEach(function (uw) {
        var shares = placedWords.some(function (pw) {
          for (var i = 0; i < uw.length; i++) if (pw.indexOf(uw.charAt(i)) !== -1) return true;
          return false;
        });
        (shares ? noFit : noShare).push(uw);
      });
      var parts = [];
      if (noShare.length) {
        parts.push(noShare.map(function (w) { return '"' + w + '"'; }).join(', ')
          + (noShare.length === 1 ? ' doesn’t' : ' don’t')
          + ' share a letter with your other words, so it can’t connect. Try a middle name, nickname, or a word like LOVE or FAMILY.');
      }
      if (noFit.length) {
        parts.push('We couldn’t find a layout that fits ' + noFit.map(function (w) { return '"' + w + '"'; }).join(', ')
          + ' without words touching. Try re-generating, or swap in a word that shares a more common letter.');
      }
      out.error = { code: noShare.length ? 'no_connection' : 'no_fit', words: best.unplaced, message: parts.join(' ') };
    }
    return out;
  }

  function generateVariants(words, k, opts) {
    opts = opts || {};
    var results = [];
    var sigs = {};
    var baseSeed = (opts.seed == null ? 1 : opts.seed) | 0;
    var tries = Math.max(k * 12, 48);
    if (!words || words.length < 2) {
      var r0 = generate(words, opts);
      return r0.ok ? [r0] : [];
    }
    for (var i = 0; i < tries && results.length < k; i++) {
      var res = attempt(words, baseSeed + i * 104729);
      if (res.unplaced.length) continue;
      var sig = signature(res);
      if (sigs[sig]) continue;
      sigs[sig] = true;
      var lc = letterCounts(res);
      results.push({ ok: true, grid: res.grid, placements: res.placements, unplaced: [],
                     letterCounts: lc.counts, tileCount: lc.total, score: res.score });
    }
    results.sort(function (a, b) { return b.score - a.score; });
    return results;
  }

  return {
    normalizeWords: normalizeWords,
    generate: generate,
    generateVariants: generateVariants,
    MAX_WORDS: MAX_WORDS,
    _internals: { canPlace: canPlace, rng: rng, attempt: attempt, GRID: GRID, MAX_WORDS: MAX_WORDS, MAX_WORD_LEN: MAX_WORD_LEN }
  };
});

/*========================================================
  Stiles Crossword Builder UI (v2)
  - live preview + pricing as words are typed (no Generate click needed)
  - wall-size readout and free-shipping progress
  - per-finish pricing + letter availability, inline errors
  - cart add with real error handling (no more silent 422s)
  - mobile sticky summary bar inside the builder modal
=========================================================*/
(function ($) {
  'use strict';

  if (!$) return;

  var TILE_INCHES = 3.5;
  var FREE_SHIP_CENTS = 6000;

  var STATE = {
    variants: [],      // precomputed distinct layouts for current words
    variantIndex: 0,   // which layout is shown (RE-GENERATE cycles)
    result: null,      // current engine result
    wordsKey: ''       // cache key for variants
  };

  /* ---------------- helpers ---------------- */

  function moneyFormat() {
    var el = document.querySelector('.installments');
    return (el && el.getAttribute('data-money-format')) || '${{amount}}';
  }

  function formatMoney(cents, format) {
    cents = parseInt(cents, 10);
    if (!isFinite(cents)) cents = 0;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = function (n, precision, thousands, decimal) {
      thousands = thousands || ','; decimal = decimal || '.';
      n = (n / 100).toFixed(precision);
      var p = n.split('.');
      var i = p[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return i + (p[1] ? decimal + p[1] : '');
    };
    var formatString = format || moneyFormat();
    var token = (formatString.match(placeholderRegex) || [, 'amount'])[1];
    var map = {
      amount: fmt(cents, 2, ',', '.'),
      amount_no_decimals: fmt(cents, 0, ',', '.'),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.')
    };
    return formatString.replace(placeholderRegex, map[token] || fmt(cents, 2));
  }

  function selectedSwatch() { return $('.crossword-swatches .swatch.selected'); }

  function selectedStyle() {
    var s = selectedSwatch().data('style');
    return s || 'Wood';
  }

  function styleLabel(style) {
    if (style === 'Wood') return 'Natural';
    return style;
  }

  // cents per tile for a given swatch element
  function tilePriceCents($swatch) {
    var raw = String($swatch.data('price') != null ? $swatch.data('price') : '').replace(',', '.');
    var f = parseFloat(raw);
    if (!isFinite(f) || f <= 0) {
      f = parseFloat($('.crossword-variant-price').attr('data-variant-price')) || 0;
    }
    return Math.round(f * 100);
  }

  // Variant select element for a style ('Wood' = base product)
  function selectForStyle(style) {
    return style === 'Wood' ? $('#ProductSelect-crossword') : $('#LinkedStyle-' + style);
  }

  // { 'A': {id: '123', available: true}, ... } for a style
  function availabilityFor(style) {
    var map = {};
    selectForStyle(style).find('option').each(function () {
      var letter = $(this).text().replace(/-.*$/, '').trim().toUpperCase();
      if (letter.length !== 1) letter = ($(this).text().trim().charAt(0) || '').toUpperCase();
      if (!/^[A-Z]$/.test(letter)) return;
      map[letter] = { id: $(this).val(), available: !$(this).is(':disabled') };
    });
    return map;
  }

  // letters (uniq) required by the current layout
  function requiredLetters() {
    return STATE.result && STATE.result.letterCounts ? Object.keys(STATE.result.letterCounts) : [];
  }

  function unavailableIn(style) {
    var avail = availabilityFor(style);
    return requiredLetters().filter(function (L) {
      return !avail[L] || !avail[L].available;
    });
  }

  // finishes that can produce the current layout
  function finishesWithAllLetters() {
    var out = [];
    $('.crossword-swatches .swatch').each(function () {
      var style = $(this).data('style');
      if (style && unavailableIn(style).length === 0) out.push(style);
    });
    return out;
  }

  /* ---------------- feedback UI ---------------- */

  function feedbackEl() {
    var $el = $('.crossword-feedback');
    if (!$el.length) {
      $el = $('<div class="crossword-feedback" role="alert" aria-live="polite"></div>');
      $('.tags_wrapper').after($el);
    }
    return $el;
  }

  function showFeedback(type, html) {
    feedbackEl().attr('data-type', type).html(html).show();
  }

  function clearFeedback() { feedbackEl().hide().empty(); }

  function availabilityMessage(style) {
    var missing = unavailableIn(style);
    if (!missing.length) return null;
    var alternatives = finishesWithAllLetters().map(styleLabel);
    var msg = 'The letter' + (missing.length > 1 ? 's ' : ' ') +
      '<strong>' + missing.join(', ') + '</strong>' +
      (missing.length > 1 ? ' are' : ' is') + ' sold out in ' + styleLabel(style) + '.';
    if (alternatives.length) {
      msg += ' All of your letters are available in ' +
        alternatives.map(function (s) {
          return '<button type="button" class="crossword-switch-finish" data-style-label="' + s + '">' + s + '</button>';
        }).join(' ') + '.';
    } else {
      msg += ' Unfortunately no finish currently has all of your letters in stock — please try different words or check back soon.';
    }
    return msg;
  }

  /* ---------------- words input ---------------- */

  function currentWords() {
    var words = [];
    $('.bootstrap-tagsinput .tag').each(function () {
      var w = $(this).text().replace(/\s*×\s*$/, '').trim();
      if (w) words.push(w);
    });
    if (!words.length) {
      // tagsinput not initialized — fall back to raw input value
      String($('#tags').val() || '').split(/[\s,]+/).forEach(function (w) {
        if (w.trim()) words.push(w.trim());
      });
    }
    return words;
  }

  function addTag(raw) {
    var pieces = String(raw || '').split(/[\s,]+/);
    pieces.forEach(function (piece) {
      piece = piece.trim();
      if (!piece) return;
      var norm = window.CrosswordEngine.normalizeWords([piece]);
      if (norm.errors.length) {
        showFeedback('error', norm.errors[0].message);
        return;
      }
      clearFeedback();
      try { $('#tags').tagsinput('add', norm.words[0]); }
      catch (e) {
        var v = $('#tags').val();
        $('#tags').val(v ? v + ',' + norm.words[0] : norm.words[0]);
      }
    });
  }

  // tokenize on Enter, Space, or comma — matches the FAQ's promise.
  // Capture phase: bootstrap-tagsinput's own keydown handler stops propagation,
  // so a bubbling-phase listener never sees the event.
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || t.tagName !== 'INPUT') return;
    if (!t.closest('.tags_wrapper') && !t.closest('.bootstrap-tagsinput')) return;
    var key = e.key;
    if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === ',') {
      e.preventDefault();
      e.stopPropagation();
      var val = t.value;
      if (val && val.trim()) { addTag(val); t.value = ''; }
    }
  }, true);
  // paste / autocomplete safety: split anything containing separators
  $(document).on('input', '.tags_wrapper input, .bootstrap-tagsinput input', function () {
    var val = String($(this).val() || '');
    if (/[\s,]/.test(val)) { addTag(val); $(this).val(''); }
  });

  /* ---------------- rendering ---------------- */

  function tileImageFor(style, letter) {
    try {
      if (window.images && window.images[style] && window.images[style][letter]) {
        return window.images[style][letter];
      }
    } catch (e) { /* noop */ }
    return '';
  }

  function renderGrid(result) {
    var style = selectedStyle();
    var html = ["<table class='crossword'>"];
    result.grid.forEach(function (row, r) {
      html.push('<tr>');
      row.forEach(function (cell, c) {
        if (!cell) {
          html.push("<td class='no-border'>&nbsp;</td>");
        } else {
          var img = tileImageFor(style, cell.char);
          html.push("<td class='letter' title='" + r + ', ' + c + "'" +
            (img ? " style=\"background-image:url(" + img + ')"' : '') + '>' + cell.char + '</td>');
        }
      });
      html.push('</tr>');
    });
    html.push('</table>');
    var target = document.getElementById('crossword');
    if (target) target.innerHTML = html.join('\n');
  }

  function clearGrid(hint) {
    var target = document.getElementById('crossword');
    if (!target) return;
    if (hint) {
      target.innerHTML = '<div class="crossword-empty-hint">Your crossword preview will appear here.<br>Add two or more words to get started.</div>';
    } else {
      target.innerHTML = '<table class="crossword"><tbody><tr><td></td></tr></tbody></table>';
    }
  }

  /* ---------------- pricing / summary ---------------- */

  function accessoriesCents() {
    var cents = 0;
    $('.product-accessories-item').each(function () {
      var f = parseFloat($(this).find('.product-accessories-price-input').val());
      if (isFinite(f)) cents += Math.round(f * 100);
    });
    if ($('.spice-spa-addon-checkbox-input').prop('checked')) {
      var addon = parseFloat($('.spice-spa-addon-price-main').attr('variant_price'));
      if (isFinite(addon)) cents += Math.round(addon); // attr is already cents
    }
    return cents;
  }

  function wallSize() {
    if (!STATE.result || !STATE.result.grid || !STATE.result.grid.length) return null;
    var rows = STATE.result.grid.length;
    var cols = STATE.result.grid[0].length;
    return { w: Math.round(cols * TILE_INCHES), h: Math.round(rows * TILE_INCHES) };
  }

  function updateStickyPdpBar(tiles, totalCents, fmt) {
    // resting state: promote the builder, not a $0.00 cart button
    var $gen = $('.fixed-block-right.generator_btn_btm');
    var $atcBlock = $('.fixed-block-right.add_cart_btn_btm');
    if (!$gen.length && !$atcBlock.length) return;
    if (tiles > 0) {
      $gen.addClass('hide');
      $atcBlock.removeClass('hide');
      $('.fixed-add-to-cart .fixed-add-to-cart-wrapper .cart-btn span').html(formatMoney(totalCents, fmt));
    } else {
      $gen.removeClass('hide');
      $atcBlock.addClass('hide');
    }
  }

  function updateModalStickyBar(tiles, totalCents, fmt) {
    var bar = document.querySelector('.crossword-mobile-bar');
    if (!bar) {
      var modal = document.getElementById('formModal');
      if (!modal) return;
      bar = document.createElement('div');
      bar.className = 'crossword-mobile-bar';
      bar.innerHTML = '<div class="cmb-info"><span class="cmb-letters"></span><span class="cmb-size"></span></div>' +
        '<button type="button" class="cmb-atc"><span class="cmb-total"></span> · Add to Cart</button>';
      var content = modal.querySelector('.modal_new-content') || modal;
      content.appendChild(bar);
      bar.querySelector('.cmb-atc').addEventListener('click', function () {
        var atc = document.querySelector('.crossword-add-to-cart');
        if (atc && !atc.disabled) atc.click();
        else if (atc) atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    var size = wallSize();
    bar.querySelector('.cmb-letters').textContent = tiles + (tiles === 1 ? ' letter' : ' letters');
    bar.querySelector('.cmb-size').textContent = size ? '~' + size.w + '″ × ' + size.h + '″' : '';
    bar.querySelector('.cmb-total').textContent = formatMoney(totalCents, fmt);
    bar.style.display = tiles > 0 ? '' : 'none';
    var atcReal = document.querySelector('.crossword-add-to-cart');
    bar.querySelector('.cmb-atc').disabled = !atcReal || atcReal.disabled;
  }

  function updateTotals() {
    var tiles = STATE.result && STATE.result.ok !== false ? (STATE.result.tileCount || 0) : 0;
    var perTile = tilePriceCents(selectedSwatch());
    var tilesCents = tiles * perTile;
    var totalCents = tilesCents + accessoriesCents();
    var fmt = moneyFormat();

    $('.total_letters').html(String(tiles));

    var priceNode = document.querySelector('.crossword-variant-price');
    if (priceNode) {
      priceNode.textContent = formatMoney(totalCents, fmt);
      priceNode.setAttribute('data-variant-price', (totalCents / 100).toFixed(2));
      priceNode.setAttribute('data-variant-price-cents', String(totalCents));
    }

    // summary: letters × price, wall size, free-shipping progress
    if (tiles > 0) {
      var size = wallSize();
      var shipHtml;
      if (totalCents >= FREE_SHIP_CENTS) {
        shipHtml = '<span class="crossword-ship ok">✓ Ships free</span>';
      } else {
        shipHtml = '<span class="crossword-ship">' + formatMoney(FREE_SHIP_CENTS - totalCents, fmt) + ' away from free shipping</span>';
      }
      $('form .crossword-selection').html(
        '<div class="crossword-summary">' +
        '<div>' + tiles + ' letters × ' + formatMoney(perTile, fmt) + ' = <strong>' + formatMoney(tilesCents, fmt) + '</strong></div>' +
        (size ? '<div class="crossword-size">Hangs about <strong>' + size.w + '″ wide × ' + size.h + '″ tall</strong></div>' : '') +
        shipHtml +
        '</div>'
      );
    } else {
      $('form .crossword-selection').empty();
    }

    // installments
    var instRoot = document.querySelector('.installments');
    if (instRoot) {
      var threshold = instRoot.querySelector('.installments__threshold');
      var calcWrap = instRoot.querySelector('.installments__calc');
      var valueNode = instRoot.querySelector('[data-installments-value]');
      var timesNode = instRoot.querySelector('.installments__times');
      if (totalCents > 5000) {
        if (timesNode) timesNode.textContent = '4 ×';
        if (valueNode) valueNode.textContent = formatMoney(Math.round(totalCents / 4), fmt);
        if (threshold) threshold.style.display = 'none';
        if (calcWrap) calcWrap.style.display = '';
      } else {
        if (threshold) threshold.style.display = '';
        if (calcWrap) calcWrap.style.display = 'none';
      }
    }

    updateStickyPdpBar(tiles, totalCents, fmt);
    updateModalStickyBar(tiles, totalCents, fmt);

    document.dispatchEvent(new CustomEvent('crossword:updated', { detail: { tiles: tiles, totalCents: totalCents } }));
  }

  function setAtcEnabled(enabled) {
    var $atc = $('.crossword-add-to-cart');
    if (enabled) $atc.removeAttr('disabled');
    else $atc.attr('disabled', 'disabled');
  }

  /* ---------------- generate flow ---------------- */

  function applyResult(result) {
    STATE.result = result;
    renderGrid(result);

    var style = selectedStyle();
    var availMsg = availabilityMessage(style);
    if (availMsg) {
      showFeedback('warn', availMsg);
      setAtcEnabled(false);
    } else {
      clearFeedback();
      setAtcEnabled(true);
    }
    updateTotals();
    $('.product-submit').addClass('active');
  }

  // quiet=true: live update while typing — no nagging errors for incomplete input
  function generate(cycle, quiet) {
    var raw = currentWords();
    // tokenize anything still sitting in the input box
    var leftover = $('.bootstrap-tagsinput input').val();
    if (leftover && leftover.trim() && !quiet) { addTag(leftover); $('.bootstrap-tagsinput input').val(''); raw = currentWords(); }

    var norm = window.CrosswordEngine.normalizeWords(raw);
    if (norm.errors.length && !quiet) {
      showFeedback('error', norm.errors.map(function (e) { return e.message; }).join('<br>'));
    }
    if (!norm.words.length) {
      if (!quiet) showFeedback('error', 'Type at least two words — names, dates spelled out, or words like LOVE and FAMILY.');
      STATE.result = null; STATE.variants = []; STATE.wordsKey = '';
      clearGrid(true); setAtcEnabled(false); updateTotals();
      return;
    }

    var key = norm.words.slice().sort().join('|');
    if (key !== STATE.wordsKey || !STATE.variants.length) {
      STATE.variants = window.CrosswordEngine.generateVariants(norm.words, 8, { seed: 7 });
      STATE.variantIndex = 0;
      STATE.wordsKey = key;
    } else if (cycle) {
      STATE.variantIndex = (STATE.variantIndex + 1) % STATE.variants.length;
    }

    if (STATE.variants.length) {
      applyResult(STATE.variants[STATE.variantIndex]);
      // The variety tip is informational; never let it overwrite a sold-out
      // warning, which is the thing actually blocking Add to Cart.
      if (STATE.variants.length === 1 && cycle && !availabilityMessage(selectedStyle())) {
        showFeedback('ok', 'These words have one ideal layout — add another word for more variety.');
      }
      return;
    }

    // no full layout possible — show partial + explain
    var res = window.CrosswordEngine.generate(norm.words, { seed: 7 });
    STATE.result = res;
    if (res.placements && res.placements.length) renderGrid(res); else clearGrid(false);
    setAtcEnabled(false);
    updateTotals();
    if (res.error) showFeedback('error', res.error.message);
  }

  var hasGenerated = false;
  $(document).on('click keydown', '.generate', function (e) {
    if (e.type === 'keydown' && e.which !== 13 && e.which !== 32) return;
    e.preventDefault();
    generate(hasGenerated, false);
    hasGenerated = true;
  });

  // LIVE preview: regenerate as chips are added/removed (Mixtiles-style feedback)
  var liveTimer = null;
  function scheduleLiveUpdate() {
    if (liveTimer) clearTimeout(liveTimer);
    liveTimer = setTimeout(function () { generate(false, true); }, 300);
  }
  $(function () {
    try {
      // Refuse words past the engine's cap up front. Otherwise the chip
      // appears but the live preview (which runs quietly) ignores it.
      $('#tags').on('beforeItemAdd', function (e) {
        var max = window.CrosswordEngine.MAX_WORDS;
        if ($('#tags').tagsinput('items').length >= max) {
          e.cancel = true;
          showFeedback('error', 'You can use up to ' + max + ' words. Remove one to add "' + e.item + '".');
        }
      });
      $('#tags').on('itemAdded itemRemoved', scheduleLiveUpdate);
    } catch (e) { /* noop */ }
    var bt = document.querySelector('.bootstrap-tagsinput');
    if (bt) {
      new MutationObserver(scheduleLiveUpdate).observe(bt, { childList: true });
    }
  });

  /* ---------------- finish (swatch) switching ---------------- */

  $(document).on('click', '.crossword-swatches .swatch', function () {
    var $sw = $(this);
    $('.crossword-swatches .swatch').removeClass('selected');
    $sw.addClass('selected');
    $('.crossword-media-dots li').removeClass('active');
    $('.crossword-dot-' + $sw.data('index')).addClass('active');

    var pre = $sw.data('pre_order');
    $('.crossword-add-to-cart').attr('value', pre ? 'Pre Order' : 'Add to cart');
    if (pre) $('.crossword-add-to-cart').addClass('lft'); else $('.crossword-add-to-cart').removeClass('lft');

    var perTile = tilePriceCents($sw);
    $('.crossword-price > .product-price-minimum')
      .data('variant-price', (perTile / 100).toFixed(2))
      .html(formatMoney(perTile, moneyFormat()) + ' per ' + styleLabel($sw.data('style')) + ' tile');

    // keep the SAME layout — just re-skin tiles, re-price, re-check stock
    if (STATE.result && STATE.result.grid) {
      renderGrid(STATE.result);
      applyResult(STATE.result);
    }
  });

  // "available in White" quick-switch buttons inside feedback messages
  $(document).on('click', '.crossword-switch-finish', function () {
    var label = $(this).data('style-label');
    $('.crossword-swatches .swatch').each(function () {
      if (styleLabel($(this).data('style')) === label) { $(this).trigger('click'); return false; }
    });
  });

  /* ---------------- add to cart ---------------- */

  function layoutRows() {
    if (!STATE.result || !STATE.result.grid) return '';
    return STATE.result.grid.map(function (row) {
      return row.map(function (cell) { return cell ? cell.char : ''; }).join(',');
    }).join('|');
  }

  function buildCartItems() {
    var style = selectedStyle();
    var avail = availabilityFor(style);
    var counts = STATE.result.letterCounts;
    var words = STATE.result.placements.map(function (p) { return p.word.toLowerCase(); }).join(' - ');
    var crosswordId = 'id-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    var items = [];
    var missing = [];
    Object.keys(counts).forEach(function (letter) {
      var entry = avail[letter];
      if (!entry || !entry.available) { missing.push(letter); return; }
      items.push({
        quantity: counts[letter],
        id: parseInt(entry.id, 10),
        properties: {
          '_Builder QTY': counts[letter],
          crossword_id: crosswordId,
          Words: words,
          Finish: styleLabel(style),
          layout: layoutRows()
        }
      });
    });
    $('.product-accessories-item').each(function () {
      var id = $(this).find('.product-accessories-id').val();
      var qty = $(this).find('.product-accessories-qty').find('select option:selected').val() || 1;
      items.push({ quantity: qty, id: parseInt(id, 10) });
    });
    return { items: items, missing: missing };
  }

  function parseAddError(xhr) {
    var msg = 'We couldn’t add that to your cart. Please try again.';
    try {
      var data = JSON.parse(xhr.responseText);
      var raw = data.description || data.message || '';
      var m = raw.match(/-\s*([A-Z])'?\s*is already sold out/i) || raw.match(/'[^']*-\s*([A-Z])'/);
      if (m) {
        var letter = m[1].toUpperCase();
        var alternatives = finishesWithAllLetters().map(styleLabel);
        msg = 'The letter <strong>' + letter + '</strong> just sold out in ' + styleLabel(selectedStyle()) + '.';
        msg += alternatives.length
          ? ' It’s still available in ' + alternatives.map(function (s) {
              return '<button type="button" class="crossword-switch-finish" data-style-label="' + s + '">' + s + '</button>';
            }).join(' ') + ' — switch and add to cart again.'
          : ' Please try a different finish or different words.';
      } else if (raw) {
        msg = raw;
      }
    } catch (e) { /* keep default */ }
    return msg;
  }

  $(document).on('click', '.payment-buttons-container input, .shopify-payment-button__button, .product-form__cart-submit-checkout, .shopify-payment-button', function (e) {
    e.preventDefault();

    if (!STATE.result || !STATE.result.ok || !STATE.result.tileCount) {
      showFeedback('error', 'Generate your crossword first — type your words and click “Generate Crossword”.');
      return;
    }

    // pre-flight availability check: catch sold-out letters BEFORE Shopify does
    var availMsg = availabilityMessage(selectedStyle());
    if (availMsg) { showFeedback('warn', availMsg); return; }

    var build = buildCartItems();
    if (build.missing.length) {
      showFeedback('warn', availabilityMessage(selectedStyle()) || ('Sold out letters: ' + build.missing.join(', ')));
      return;
    }

    var $atc = $('.crossword-add-to-cart');
    var prevVal = $atc.val();
    $atc.val('Adding…').attr('disabled', 'disabled');

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: { items: build.items },
      dataType: 'json'
    }).done(function () {
      clearFeedback();
      $('.product-accessories').find('.product-accessories-wrapper').empty();
      $('.total_accessories').html('0');
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    }).fail(function (xhr) {
      showFeedback('error', parseAddError(xhr));
    }).always(function () {
      $atc.val(prevVal).removeAttr('disabled');
    });
  });

  /* ---------------- screenshot button (unchanged behavior) ---------------- */

  $(document).on('click', '.screenshot-button', function () {
    if (typeof html2canvas === 'undefined') return;
    $('.letter').addClass('dark-text');
    html2canvas(document.querySelector('.crossword'), {
      onrendered: function (canvas) {
        setTimeout(function () { $('.letter').removeClass('dark-text'); }, 500);
        if (typeof Canvas2Image !== 'undefined') return Canvas2Image.saveAsJPEG(canvas);
      }
    });
  });

  /* ---------------- on-wall mockup preview (photo composite) ---------------- */
  // Photographic room scene (assets/crossword-room-scene.jpg) with the
  // customer's crossword composited on the wall at true relative scale.
  // Calibration, measured against the sofa in the photo (assumed 84″ wide):
  //   - the image frame spans ~147″ of wall horizontally
  //   - the wall/floor line sits at 81% of the image height
  //   - art hangs centered at 58″ above the floor (standard gallery height)
  var ROOM = {
    aspect: 1800 / 1207,   // scene image w/h
    wallSpanIn: 147,
    floorYFrac: 0.81,
    artCenterIn: 58
  };

  // Real product letter photos with their REAL studio drop shadows preserved:
  // each 340px sprite frame holds the 260px tile face centered, surrounded by
  // the photo's own shadow converted to true alpha (white bg keyed out), so
  // shadows composite naturally onto the wall photo. A..Z left to right.
  var LETTER_SPRITES = {
    Wood:   'crossword-letters-nat.webp',
    White:  'crossword-letters-wht.webp',
    Rustic: 'crossword-letters-rus.webp',
    Walnut: 'crossword-letters-wal.webp'
  };
  var SPRITE_OVERHANG = 340 / 260; // frame size relative to tile face

  // theme asset URL, derived from our own script tag (version-safe)
  function themeAssetUrl(name) {
    var s = document.querySelector('script[src*="crossword-scripts"]');
    if (s && s.src) return s.src.replace(/crossword-scripts\.js[^\/]*$/, name);
    return name;
  }

  function injectWallCss() {
    if (document.getElementById('crossword-wall-css')) return;
    var css = '' +
      '.crossword-wall-btn{display:inline-flex;align-items:center;gap:8px;margin:10px auto 0;padding:10px 18px;' +
      'background:#fff;border:1px solid #c7592c;border-radius:4px;color:#c7592c;font-size:14px;font-weight:600;cursor:pointer;}' +
      '.crossword-wall-btn:hover{background:#fdf3ec;}' +
      '.crossword-wall-overlay{position:fixed;inset:0;z-index:100001;background:rgba(20,16,12,.6);display:flex;align-items:center;justify-content:center;padding:16px;}' +
      '.crossword-wall-scene{position:relative;border-radius:10px;overflow:hidden;box-shadow:0 18px 60px rgba(0,0,0,.4);' +
      'background-size:cover;background-position:center;}' +
      '.crossword-wall-grid{position:absolute;display:grid;}' +
      '.crossword-wall-grid span.tile{position:relative;}' +
      // inner layer extends past the face so the photo's real drop shadow shows;
      // no synthetic box-shadow — the photography carries the light.
      '.crossword-wall-grid span.tile i{position:absolute;display:block;' +
      'left:-15.385%;top:-15.385%;width:130.77%;height:130.77%;' +
      'background-size:2600% 100%;background-repeat:no-repeat;}' +
      '.crossword-wall-close{position:absolute;top:8px;right:12px;background:rgba(255,255,255,.85);border:none;border-radius:99px;' +
      'width:34px;height:34px;font-size:22px;line-height:1;color:#3c352c;cursor:pointer;z-index:2;}' +
      '.crossword-wall-caption{position:absolute;bottom:12px;left:50%;transform:translateX(-50%);white-space:nowrap;' +
      'background:rgba(255,255,255,.92);border-radius:99px;padding:8px 18px;color:#5c544a;font-size:13px;z-index:1;box-shadow:0 2px 8px rgba(0,0,0,.12);}' +
      '.crossword-wall-caption strong{color:#2b2620;}' +
      '@media (max-width:600px){.crossword-wall-caption{white-space:normal;text-align:center;width:86%;}}';
    var style = document.createElement('style');
    style.id = 'crossword-wall-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function openWallPreview() {
    if (!STATE.result || !STATE.result.grid) return;
    injectWallCss();
    var size = wallSize();
    var style = selectedStyle();

    var overlay = document.createElement('div');
    overlay.className = 'crossword-wall-overlay';
    var scene = document.createElement('div');
    scene.className = 'crossword-wall-scene';
    scene.style.backgroundImage = 'url(' + themeAssetUrl('crossword-room-scene.jpg') + ')';
    overlay.appendChild(scene);
    document.body.appendChild(overlay);

    // size the scene to the photo's aspect, fitting the viewport
    var W = Math.min(960, window.innerWidth * 0.94);
    var H = W / ROOM.aspect;
    var maxH = window.innerHeight * 0.84;
    if (H > maxH) { H = maxH; W = H * ROOM.aspect; }
    scene.style.width = Math.round(W) + 'px';
    scene.style.height = Math.round(H) + 'px';

    var ppi = W / ROOM.wallSpanIn;                 // pixels per inch of wall in the photo
    var rows = STATE.result.grid.length, cols = STATE.result.grid[0].length;
    var cell = 3.5 * ppi;
    var artW = cols * cell, artH = rows * cell;
    var floorY = H * ROOM.floorYFrac;
    var top = floorY - ROOM.artCenterIn * ppi - artH / 2;
    top = Math.max(H * 0.04, top);                 // keep large pieces inside the frame

    var gridEl = document.createElement('div');
    gridEl.className = 'crossword-wall-grid';
    gridEl.style.gridTemplateColumns = 'repeat(' + cols + ',' + cell.toFixed(2) + 'px)';
    gridEl.style.gridAutoRows = cell.toFixed(2) + 'px';
    gridEl.style.left = ((W - artW) / 2).toFixed(1) + 'px';
    gridEl.style.top = top.toFixed(1) + 'px';
    var spriteUrl = themeAssetUrl(LETTER_SPRITES[styleLabel(style)] || LETTER_SPRITES.Wood);
    STATE.result.grid.forEach(function (row) {
      row.forEach(function (cellData) {
        var s = document.createElement('span');
        if (cellData) {
          var idx = cellData.char.charCodeAt(0) - 65; // A=0 .. Z=25
          s.className = 'tile';
          s.setAttribute('aria-label', cellData.char);
          var inner = document.createElement('i');
          inner.style.backgroundImage = 'url(' + spriteUrl + ')';
          inner.style.backgroundPosition = (idx * 4).toFixed(0) + '% 0'; // idx/25 * 100
          s.appendChild(inner);
        }
        gridEl.appendChild(s);
      });
    });
    scene.appendChild(gridEl);

    var caption = document.createElement('div');
    caption.className = 'crossword-wall-caption';
    caption.innerHTML = 'Shown at actual size — hangs <strong>' +
      (size ? size.w + '″ wide × ' + size.h + '″ tall' : '') + '</strong> in ' + styleLabel(style) +
      ' <span style="opacity:.65">· sofa shown: 7 ft</span>';
    scene.appendChild(caption);

    var close = document.createElement('button');
    close.className = 'crossword-wall-close';
    close.setAttribute('aria-label', 'Close wall preview');
    close.innerHTML = '&times;';
    scene.appendChild(close);

    function destroy() { overlay.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(e) { if (e.key === 'Escape') destroy(); }
    close.addEventListener('click', destroy);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) destroy(); });
    document.addEventListener('keydown', onKey);
  }

  function ensureWallButton() {
    if (document.querySelector('.crossword-wall-btn')) return;
    var anchor = document.getElementById('crossword');
    if (!anchor || !anchor.parentElement) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'crossword-wall-btn';
    btn.innerHTML = '&#128715; See it on your wall';
    btn.addEventListener('click', openWallPreview);
    anchor.parentElement.appendChild(btn);
  }

  // Feature flag: the on-wall mockup is still being refined. Set true to
  // re-enable the "See it on your wall" button under the builder preview.
  var WALL_PREVIEW_ENABLED = false;

  document.addEventListener('crossword:updated', function (e) {
    if (!WALL_PREVIEW_ENABLED) return;
    var tiles = e.detail && e.detail.tiles;
    if (tiles > 0) { ensureWallButton(); $('.crossword-wall-btn').show(); }
    else $('.crossword-wall-btn').hide();
  });

  /* ---------------- desktop: fit the builder modal to the screen ---------------- */

  // Preview tiles always shrink to fit their box (wide designs used to run off
  // phone screens). On desktop the whole builder must also be visible with no
  // scrolling, so if it is still taller than the viewport the modal scales down.
  var FIT_MIN_SCALE = 0.6;
  var fitObserver = null, fitTimer = null;

  function isDesktop() { return window.matchMedia('(min-width: 1024px)').matches; }

  function sizePreview(maxH) {
    var table = document.querySelector('#crossword table.crossword');
    var box = document.getElementById('crossword');
    if (!table || !box || !table.rows.length) return;
    var rows = table.rows.length, cols = table.rows[0].cells.length;
    var availW = box.clientWidth;
    if (!availW) return;
    var tile = Math.min(40, Math.floor(availW / cols), Math.floor(maxH / rows));
    // set on the container, which survives re-renders of the table
    box.style.setProperty('--cw-tile', Math.max(12, tile) + 'px');
  }

  function fitModal() {
    var modal = document.getElementById('formModal');
    var content = modal && modal.querySelector('.modal_new-content');
    if (!content) return;
    content.style.zoom = '';
    if (!content.offsetParent) {
      if (fitObserver) fitObserver.takeRecords();
      return;
    }
    content.classList.toggle('crossword-has-words', currentWords().length > 0);
    if (!isDesktop()) {
      sizePreview(Math.round(window.innerHeight * 0.34));
      if (fitObserver) fitObserver.takeRecords();
      return;
    }

    var margins = 32;
    var regen = modal.querySelector('.product-gallery .single-generate-button, .media_temp .generate');
    var reserve = margins + 30 + (regen ? regen.offsetHeight + 40 : 80);
    sizePreview(window.innerHeight - reserve);

    var needed = content.getBoundingClientRect().height;
    var avail = window.innerHeight - margins;
    var scale = needed > avail ? Math.max(FIT_MIN_SCALE, avail / needed) : 1;
    content.style.zoom = scale < 1 ? scale.toFixed(3) : '';
    if (fitObserver) fitObserver.takeRecords();
  }

  function scheduleFit() {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(fitModal, 30);
  }

  $(function () {
    var modal = document.getElementById('formModal');
    if (!modal || !window.MutationObserver) return;
    fitObserver = new MutationObserver(scheduleFit);
    fitObserver.observe(modal, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', scheduleFit);
  });

  /* ---------------- boot ---------------- */

  // Accessory rows (hanging strips etc.) and add-ons are managed by the
  // theme section; it tells us when they change so the total stays in sync.
  document.addEventListener('crossword:accessories-changed', function () { updateTotals(); });

  $(function () {
    // start clean: no placeholder words, disabled ATC, empty preview
    setAtcEnabled(false);
    if (!$('.bootstrap-tagsinput .tag').length) clearGrid(true);
    updateTotals();
  });

})(window.jQuery);
